import React, { RefObject, createRef } from 'react';
import { RouteObject } from 'react-router-dom';
import { IconProps } from './components/icons';
import ErrorPage from './ErrorPage';
import HomeIcon from '@mui/icons-material/RoofingOutlined';
import ReceiptsIcon from '@mui/icons-material/VerifiedUserOutlined';
import InboxIcon from '@mui/icons-material/MarkunreadMailboxOutlined';
import ReportsIcon from '@mui/icons-material/DownloadingOutlined';
import SettingsIcon from '@mui/icons-material/SettingsOutlined';
import AuthProtected from './components/AuthProtected';
import Home from './routes/Root';
import Receipts from './routes/Receipts';
import Inbox from './routes/Inbox';
import Reports from './routes/Reports/view';
import Settings from './routes/Settings/view';
import Login from './routes/Login';
import Dashboard from './routes/Dashboard';
import { SettingsTabs } from './routes/Settings/store/settingsViewSlice/types';
import { invert } from 'lodash';

/**
 * App Routes
 */
export enum Routes {
  Root = '/',
  Login = '/login',
  Receipts = '/receipts',
  Inbox = '/inbox',
  Reports = '/reports',
  Settings = '/settings',
  SettingsProfile = '/settings/profile',
  SettingsSubscriptions = '/settings/subscriptions',
  AuthCallback = '/auth/callback',
  AuthError = '/auth/error',
}

export interface RouteConfig {
  route: Routes;
  Icon: React.ComponentType<IconProps>;
  name: string;
}
export const routesConfigs: RouteConfig[] = [
  { route: Routes.Root, Icon: HomeIcon, name: 'בית' },
  { route: Routes.Receipts, Icon: ReceiptsIcon, name: 'החשבוניות שלי' },
  { route: Routes.Inbox, Icon: InboxIcon, name: 'תיבות דואר' },
  { route: Routes.Reports, Icon: ReportsIcon, name: 'דוחות' },
  { route: Routes.SettingsProfile, Icon: SettingsIcon, name: 'החשבון שלי' },
];

export const routesElements: RouteObject[] = [
  {
    path: '/',
    element: (
      <AuthProtected>
        <Home />
      </AuthProtected>
    ),
    errorElement: <ErrorPage />,
    children: [
      {
        path: '',
        element: <Dashboard />,
      },
      {
        path: 'receipts',
        element: <Receipts />,
      },
      {
        path: 'inbox',
        element: <Inbox />,
      },
      {
        path: 'reports',
        element: <Reports />,
      },
      {
        path: 'settings',
        element: <Settings />,
      },
    ],
  },
  {
    path: '/login',
    element: <Login />,
  },
];

export const routesNodeRefs: { [key in Routes]: RefObject<any> } = (() => {
  const settingsRef = createRef();
  return {
    '/': createRef(),
    '/login': createRef(),
    '/receipts': createRef(),
    '/inbox': createRef(),
    '/reports': createRef(),
    '/settings': settingsRef,
    '/settings/profile': settingsRef,
    '/settings/subscriptions': settingsRef,
    '/auth/callback': createRef(),
    '/auth/error': createRef(),
  };
})();

export type SettingsRoutes =
  | Routes.Settings
  | Routes.SettingsProfile
  | Routes.SettingsSubscriptions;

const settingsRoutes: SettingsRoutes[] = [
  Routes.Settings,
  Routes.SettingsProfile,
  Routes.SettingsSubscriptions,
];

export const isInSettingsGroup = (path: string) =>
  settingsRoutes.some((route) => path.startsWith(route));

export const isSettingsRouteGroup = (pathname1: string, pathname2: string) => {
  return isInSettingsGroup(pathname1) && isInSettingsGroup(pathname2);
};

// Settings tab to route map
export const settingsRouteToTabMap: Record<SettingsRoutes, SettingsTabs> = {
  [Routes.Settings]: SettingsTabs.General,
  [Routes.SettingsProfile]: SettingsTabs.Profile,
  [Routes.SettingsSubscriptions]: SettingsTabs.Subscriptions,
};

export const settingTabToRouteMap = invert(settingsRouteToTabMap) as Record<
  SettingsTabs,
  SettingsRoutes
>;
